import { Button } from '@ps/sports-ui-component-library';
import type { Currency } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import type {
  CashoutQuote,
  MyBet,
  SuspendedCashoutQuote,
  UnavailableCashoutQuote
} from '@ps/web-sports-api-types';
import React from 'react';
import { useSelector } from 'react-redux';
import type { CashOutBetState } from 'widgets/sports-my-bets-widget/model/types';

import { useSportsMyBetsWidget } from '../../../../../../model';

import { TXTCLI_Suspended, TXTWCL_Cash_Out } from './CashOut.messages.json';
import CashOutAvailable from './components/CashOutAvailable/CashOutAvailable';
import CashOutContainer from './components/CashOutContainer/CashOutContainer';
import CashOutError from './components/CashOutError/CashOutError';

interface Props {
  bet: MyBet;
  currency?: Currency;
}

const suspendedAndUnavailableStatuses: (
  | UnavailableCashoutQuote
  | SuspendedCashoutQuote
)['quoteStatus'][] = ['QUOTE_TOO_LOW', 'QUOTE_TOO_LOW_FREE_BET', 'LEG_RESULT_PENDING', 'SUSPENDED'];

const CashOut: React.FC<Props> = ({ bet, currency }) => {
  const { widgetSelect } = useSportsMyBetsWidget();
  const cashOutBetState = useSelector(widgetSelect.cashOutBets)[bet.betId] as
    | CashOutBetState
    | undefined;
  const cashOutError = cashOutBetState?.cashOutError;
  const cashOutQuote = useSelector(widgetSelect.cashOutQuotes)[bet.betId] as
    | CashoutQuote
    | undefined;

  if (!cashOutQuote) {
    return null;
  }

  const isCashoutSuspendedOrUnavailable =
    cashOutQuote.quoteStatus !== 'AVAILABLE' &&
    suspendedAndUnavailableStatuses.includes(cashOutQuote.quoteStatus);

  return (
    <>
      {cashOutQuote.quoteStatus === 'AVAILABLE' && (
        <CashOutAvailable bet={bet} cashOutQuote={cashOutQuote} currency={currency} />
      )}

      {isCashoutSuspendedOrUnavailable && (
        <CashOutContainer title={<Translation {...TXTWCL_Cash_Out} />} hasError={!!cashOutError}>
          <Button disabled size="medium">
            <Translation {...TXTCLI_Suspended} />
          </Button>
        </CashOutContainer>
      )}

      {cashOutError && <CashOutError error={cashOutError} betId={bet.betId} />}
    </>
  );
};

export default CashOut;
